import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./ErpDemoPage.module.css";
import YoutubeEmbed from "../../components/youtubeembed/YoutubeEmbed";
import Buttons from "../../components/buttons/Buttons";
import { FiArrowLeft } from "react-icons/fi";
const ErpDemoPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const sectionRefs = useRef({});

  const handleBookDemo = () => {
    navigate("/lets-talk");
    window.scrollTo(0, 0);
  };
  const erpDemoVideos = [
    {
      id: "tin-container", // 👈 anchor id
      title: "ERP Demo - Tin Container Manufacturing",
      subtitle: "TinPack Industries | 4L Round Paint Container",
      url: "https://youtu.be/5C96SYH34r0?si=irDIYYcAm3gn1ZzP",
      content: [
        {
          type: "paragraph",
          text: "A 41-minute live demo showing how a tin container factory runs on ERP - from a customer order to raw material purchase, quality inspection, manufacturing across 3 production lines, and final delivery.",
        },
        { type: "heading", text: "The Company" },
        {
          type: "paragraph",
          text: "TinPack Industries Pune is the demo company which produces 15,000 round paint containers per day for leading paint manufacturers. The product is a 4-litre round tin with three components: a printed body, a sealed bottom, and a press-fit lid.",
        },
        { type: "heading", text: "What the Demo Covers" },
        { type: "subheading", text: "Engineering & Material Economics" },
        {
          type: "paragraph",
          text: "8-page sheet cutting analysis on how a tinplate sheet becomes a paint container. Yield calculations, scrap analysis (0.4% on body line, 37% on circular punching), and per-unit costing at Rs 42.85 per container set.",
        },
        { type: "subheading", text: "Sales Cycle" },
        {
          type: "paragraph",
          text: "Sales Order from a leading paint manufacturer for 15,000 containers and lids worth Rs 12 lakhs → Delivery Note for 14,100 units (partial delivery with backlog tracking) → Sales Invoice.",
        },
        { type: "subheading", text: "Purchase Cycle with Quality Gates" },
        {
          type: "paragraph",
          text: "Purchase Order to the raw material vendor for 3,500 sheets → Purchase Receipt with incoming quality inspection → Body sheets accepted (all 2,000 passed), lid sheets partially rejected (200 numbers failed for edge waviness and is automatically segregated to Rejected Store).",
        },
        {
          type: "subheading",
          text: "Manufacturing - 3 Parallel Lines, 9 Operations",
        },
        {
          type: "labeledList",
          items: [
            {
              label: "Body Line:",
              text: "Slitting → Internal Epoxy Coating → External White Primer → Offset Printing → Punching (5 operations, 5 operators)",
            },
            {
              label: "Bottom Line:",
              text: "Slitting → Circular Punching (2 operations)",
            },
            {
              label: "Lid Line:",
              text: "Slitting → Circular Punching (2 operations)",
            },
          ],
        },
        {
          type: "paragraph",
          text: "Each operation tracked through individual Job Cards with operator names, time logs, and scrap quantities.",
        },
        { type: "subheading", text: "7-Level Bill of Materials" },
        {
          type: "paragraph",
          text: "Multi-level BOM from raw tinplate sheet through coated strips, printed strips, body blanks, bottom blanks, and lid blanks to the finished container - all visible in an interactive tree view.",
        },
        { type: "subheading", text: "Quality Inspection at Every Stage" },
        {
          type: "paragraph",
          text: "6 inspection templates - incoming (tinplate sheet), in-process (strip, coating, blank), and outgoing (sealed container). Automatic pass/fail determination from measured readings against specifications.",
        },
        { type: "subheading", text: "Assembly & Delivery" },
        {
          type: "paragraph",
          text: "Body blank + bottom blank → sealed container body → finished goods. Outgoing QI clearance before dispatch. 14,100 containers delivered - 94% yield across 5 operations with scrap tracked at every step.",
        },
        { type: "subheading", text: "Accounting" },
        {
          type: "paragraph",
          text: "Every transaction - purchase, manufacture, delivery - automatically posts to the General Ledger. Trial Balance, P&L, and Balance Sheet generated in real time.",
        },
        { type: "heading", text: "Industries This Applies To" },
        {
          type: "list",
          items: [
            "Tin container & metal packaging",
            "Paint tin manufacturing",
            "Food & edible oil tin packaging",
            "Aerosol can manufacturing",
            "Crown cap & closure manufacturing",
            "Sheet metal stamping & forming",
          ],
        },
        // 🔗 Industry-specific live demo
        {
          type: "liveDemo",
          title: "Want to Explore the Live Demo?",
          text: "We've set up a read-only demo site with all the transactions shown in the video. Browse the system at your own pace - Sales Orders, Work Orders, Molds, NOK Rejection Logs, everything.",
          url: "https://tinpack.bharatclouds.com/",
          urlLabel: "tinpack.bharatclouds.com",
          email: "info@bharatclouds.com",
        },
      ],
    },
    {
      id: "plastic-moulding", // 👈 anchor id
      title: "ERP Demo – Injection Molding in Plastic Manufacturing",
      subtitle:
        "PlasticPack Industries | Shampoo Bottles + Caps | FMCG & Pharma Packaging",
      url: "https://www.youtube.com/watch?v=AWn5S1Qbc7I",
      content: [
        {
          type: "paragraph",
          text: "A 25-minute complete walkthrough of our ERP system configured for a plastic injection molding company.",
        },
        { type: "heading", text: "The Company" },
        {
          type: "paragraph",
          text: "PlasticPack Industries, Pune is a demo company - an injection molding unit producing 50,000+ pieces per day across 11 molding machines (80T to 450T). They serve FMCG clients and pharma companies. The product range includes shampoo bottles, flip-top caps, edible oil bottles, cosmetic jars, pharma vials, dropper assemblies, and measuring cups.",
        },
        { type: "heading", text: "What the Demo Covers" },
        { type: "subheading", text: "Product & BOM Structure" },
        {
          type: "paragraph",
          text: "27 items organized into Polymer Resins, Masterbatch, Additives, Containers & Bottles, Caps & Closures, and Pharma Packaging. Each finished good has a Bill of Materials with weight-based raw material consumption - for example, a 500ml shampoo bottle uses 25g of PP Homopolymer and 1g of white masterbatch per piece. 13 BOMs, each with a Molding operation linked to the Injection Molding Machine workstation type.",
        },
        { type: "subheading", text: "Machines & Molds" },
        {
          type: "paragraph",
          text: "11 injection molding machines from 80T to 450T, each with hourly operating costs split into electricity, labour, rent, and consumables - flowing directly into per-piece product costing. 25 molds tracked in the Mold Master with total cavities, running cavities, customer ownership, current location (machine floor / tool room / maintenance), and mold age. When a cavity goes offline, the system knows output per cycle drops.",
        },
        { type: "subheading", text: "Sales Cycle" },
        {
          type: "paragraph",
          text: "Sales Order from Suryan Consumer Care for 10,000 shampoo bottles and 10,000 flip-top caps worth ₹73,000 → Manufacturing → Quality Inspection → Delivery Note → full order fulfilment tracked as “To Bill” status.",
        },
        { type: "subheading", text: "Purchase Cycle" },
        {
          type: "paragraph",
          text: "Purchase Order to Reliance Polymers for 300 Kg PP Homopolymer, 50 Kg PP Copolymer, and 10 Kg white masterbatch worth ₹42,700 → Purchase Receipt with materials landing in dedicated warehouses (Plastic Granules, Masterbatch) - stock, purchase status, and accounting all updated from one document.",
        },
        {
          type: "subheading",
          text: "Manufacturing - Work Orders, Job Cards & Material Flow",
        },
        {
          type: "paragraph",
          text: "Two Work Orders created from the Sales Order - one for bottles on IMM-06 (250T press), one for caps on IMM-03 (150T press). Job Cards track operator name, shift timing, and completed quantity. Material flow in two steps: Material Transfer moves raw material from stores to WIP, then Manufacture entry consumes WIP material and creates finished goods - 10,000 bottles and 10,000 caps into the FG-FMCG warehouse.",
        },
        {
          type: "subheading",
          text: "NOK Rejection Tracking - The Key Differentiator",
        },
        {
          type: "paragraph",
          text: "Shift-wise NOK Rejection Log capturing every defect category - Short Fill (45), Flash (35), Burn Mark (20), Colour Change (10), Damage (15), Foreign Material (5) - out of 5,200 pieces produced. 2.5% rejection rate with full process parameter capture: zone temperatures (210–220°C), injection pressure (85 bar), cooling time (12s), cycle time (18s). Hourly 2-hour interval breakdown shows exactly when quality started drifting. Root cause analysis and corrective action documented and closed.",
        },
        { type: "subheading", text: "Quality Inspection" },
        {
          type: "paragraph",
          text: "6-parameter inspection on a sample of 50 pieces - weight, height, neck diameter, wall thickness, visual appearance, and drop test from 1.2 metres. All within spec. Status: Accepted. If any parameter fails, the system can block dispatch until resolved.",
        },
        { type: "subheading", text: "Mold Lifecycle Management" },
        {
          type: "paragraph",
          text: "Tool Travel Cards track every mold mount/dismount - pickup time, machine assignment, cavity selection, first-piece inspection, and process parameter verification before production starts. Tool Correction Work Orders handle mold repairs with problem description, corrective action, and handover tracking. Mold status updates automatically.",
        },
        { type: "subheading", text: "Machine Breakdown & Maintenance" },
        {
          type: "paragraph",
          text: "10 pre-configured breakdown reasons specific to injection molding - heater band failure, hydraulic oil leak, nozzle blockage, mold clamping issues, cooling water failure, and more. Each breakdown links to a Maintenance Work Order with checkpoints, parts requirements, and corrective actions.",
        },
        { type: "subheading", text: "Automated Alerts" },
        {
          type: "paragraph",
          text: "90+ server scripts powering Telegram and email alerts for high NOK rejection rates, production shortfalls, machine breakdowns, low stock, purchase order submissions, and quality inspection results. Configurable thresholds - currently 5% NOK rejection and 10% production shortfall.",
        },
        { type: "heading", text: "Industries This Applies To" },
        {
          type: "list",
          items: [
            "Plastic injection molding - FMCG packaging",
            "Plastic injection molding - pharma packaging",
            "PET preform & bottle manufacturing",
            "Closure & cap manufacturing",
            "Blow molding - containers, drums, jerry cans",
            "Automotive plastic components",
            "Electrical insulator & connector molding",
            "Any high-volume molding operation with cavity-level quality tracking needs",
          ],
        },
        // 🔗 Industry-specific live demo
        {
          type: "liveDemo",
          title: "Want to Explore the Live Demo?",
          text: "We've set up a read-only demo site with all the transactions shown in the video. Browse the system at your own pace - Sales Orders, Work Orders, Molds, NOK Rejection Logs, everything.",
          url: "https://plasticpack.bharatclouds.com",
          urlLabel: "plasticpack.bharatclouds.com",
          email: "info@bharatclouds.com",
        },
      ],
    },
  ];
  // Hash from URL e.g. "#tin-container" → "tin-container"
  const activeHash = location.hash.replace("#", "");

  // Scroll to section + force open the matching accordion
  useEffect(() => {
    if (!activeHash) return;

    // Small delay so YouTube iframe + DOM is fully painted before scroll
    const timer = setTimeout(() => {
      const el = sectionRefs.current[activeHash];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [activeHash]);
  const showBackButton = !!activeHash;
  return (
    <>
      <div className={styles.page}>
        {showBackButton && (
          <div
            className={styles.backButton}
            onClick={() => navigate("/erp-managed-services")}
          >
            <FiArrowLeft size={18} />
            <span>Back to ERP Solutions</span>
          </div>
        )}
        {erpDemoVideos.map((video, i) => {
          // Open this card if it matches the hash; otherwise only the first is open
          const isHashMatch = activeHash === video.id;
          const shouldOpen = activeHash ? isHashMatch : i === 0;

          return (
            <div
              key={video.id}
              id={video.id}
              ref={(el) => (sectionRefs.current[video.id] = el)}
              style={{ scrollMarginTop: "100px" }} // 👈 offset for fixed navbar
            >
              <YoutubeEmbed
                title={video.title}
                subtitle={video.subtitle}
                url={video.url}
                content={video.content}
                defaultOpen={shouldOpen}
              />
            </div>
          );
        })}

        {/* 📞 Common Book a Demo CTA */}
        <section className={styles.cta}>
          <div className={styles.ctaText}>
            <h3 className={styles.ctaTitle}>
              Ready to see this running for your factory?
            </h3>
            <p className={styles.ctaSubtitle}>
              Book a personalized walkthrough with our team.
            </p>
          </div>
          <div className={styles.ctaAction}>
            <Buttons text="Book a Demo" onClick={handleBookDemo} />
          </div>
        </section>
      </div>
    </>
  );
};

export default ErpDemoPage;
